import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type FilterChip = {
  key: string;
  label: string;
  count?: number | null;
  leading?: ReactNode;
  disabled?: boolean;
};

export type FilterChipsProps = {
  chips: FilterChip[];
  value: string | null | undefined;
  onChange: (key: string) => void;
  /** filled = solid primary when active; outline = bordered chip */
  variant?: 'filled' | 'outline';
  /** touch = >=44px target, readable type (POS checkout touchscreen surfaces). */
  size?: 'default' | 'sm' | 'touch';
  wrap?: boolean;
  /** When set, active chips use this color instead of primary (tenant-branded menus). */
  accent?: string;
  className?: string;
};

/**
 * Horizontal filter chips — categories, sections, status, date presets.
 * Distinct from TabBar (page navigation). One selected chip at a time.
 */
export function FilterChips({
  chips,
  value,
  onChange,
  variant = 'filled',
  size = 'default',
  wrap = false,
  accent,
  className,
}: FilterChipsProps) {
  return (
    <div
      role="listbox"
      aria-orientation="horizontal"
      className={cn(
        'flex gap-2',
        wrap ? 'flex-wrap' : 'flex-nowrap overflow-x-auto pb-1 scrollbar-none',
        className,
      )}
    >
      {chips.map((chip) => {
        const active = value === chip.key;
        return (
          <button
            key={chip.key}
            type="button"
            role="option"
            aria-selected={active}
            disabled={chip.disabled}
            onClick={() => onChange(chip.key)}
            style={active && accent ? { background: accent, color: '#fff', borderColor: accent } : undefined}
            className={cn(
              'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:pointer-events-none disabled:opacity-50',
              size === 'sm'
                ? 'px-3 py-1 text-xs min-h-[32px] sm:min-h-0'
                : size === 'touch'
                  ? 'px-5 py-2.5 text-base min-h-[44px]'
                  : 'px-4 py-1.5 text-sm min-h-[40px] sm:min-h-0',
              variant === 'outline' && 'border',
              !accent && active && variant === 'filled' && 'bg-primary text-primary-foreground shadow-sm',
              !accent && active && variant === 'outline' && 'border-primary bg-primary text-primary-foreground',
              !active && variant === 'filled' && 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground',
              !active && variant === 'outline' && 'border-border bg-background text-muted-foreground hover:bg-accent',
            )}
          >
            {chip.leading}
            {chip.label}
            {chip.count != null && (
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-xs font-semibold',
                  active ? 'bg-background/20' : 'bg-background text-muted-foreground',
                )}
              >
                {chip.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
